import { useState, FormEvent, useEffect } from 'react';
import { sendGA4Event } from '../../../hooks/useGA4';
import { submitToDatabase, type FormData as LPFormData } from '../../../utils/lpTracking';

// === LP固有の設定（このLPでのみ変更） ===
// LP_ID は lpTracking.ts 内で URL から自動取得するため、ここでは設定不要
const FORM_FIELDS = ["qualification", "birth_year", "birth_month", "birth_day", "name", "postal_code", "phone"];

const qualifications = ['正看護師', '准看護師', '保健師', '助産師'];

// 西暦の選択肢（1940〜2010年）
const years = Array.from({ length: 71 }, (_, i) => 2010 - i);
const months = Array.from({ length: 12 }, (_, i) => i + 1);
const days = Array.from({ length: 31 }, (_, i) => i + 1);

interface ConversionFormProps {
  isOpen: boolean;
  onClose: () => void;
}

export default function ConversionForm({ isOpen, onClose }: ConversionFormProps) {
  const [currentStep, setCurrentStep] = useState(1);
  const [formData, setFormData] = useState({
    qualifications: [] as string[],
    birthYear: '',
    birthMonth: '',
    birthDay: '',
    name: '',
    postalCode: '',
    phone: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [submitError, setSubmitError] = useState(false);

  const totalSteps = 4;

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  const handleQualificationChange = (value: string) => {
    setFormData((prev) => ({
      ...prev,
      qualifications: prev.qualifications.includes(value)
        ? prev.qualifications.filter((q) => q !== value)
        : [...prev.qualifications, value],
    }));
  };

  const canProceedStep1 = formData.qualifications.length > 0;
  const canProceedStep2 =
    formData.birthYear !== '' &&
    formData.birthMonth !== '' &&
    formData.birthDay !== '';
  const canProceedStep3 =
    formData.name.trim() !== '' && formData.postalCode.replace(/\D/g, '').length === 7;
  const canSubmit =
    formData.phone.replace(/\D/g, '').length === 11;

  const handleNextStep = (from: number, to: number) => {
    const eventMap: Record<number, string> = {
      1: 'nijikango_click_1_1',
      2: 'nijikango_click_1_2',
      3: 'nijikango_click_1_3',
      4: 'nijikango_click_1_4',
    };
    const eventName = eventMap[from];
    if (eventName) {
      sendGA4Event(eventName);
    }
    setCurrentStep(to);
  };

  const handlePostalCodeChange = (value: string) => {
    // 数字とハイフンのみ許可、最大8文字（7桁＋ハイフン1つ）
    const cleaned = value.replace(/[^\d-]/g, '');
    const digits = cleaned.replace(/\D/g, '');
    if (digits.length <= 7) {
      setFormData((prev) => ({ ...prev, postalCode: cleaned }));
    }
  };

  const handlePhoneChange = (value: string) => {
    // 数字とハイフンのみ許可、最大13文字（11桁＋ハイフン2つ）
    const cleaned = value.replace(/[^\d-]/g, '');
    const digits = cleaned.replace(/\D/g, '');
    if (digits.length <= 11) {
      setFormData((prev) => ({ ...prev, phone: cleaned }));
    }
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (!canSubmit || isSubmitting) return;

    sendGA4Event('nijikango_click_1_4');

    setIsSubmitting(true);
    setSubmitError(false);

    // === 既存のフォーム送信（Readdy Form） ===
    const submitData = new URLSearchParams();
    submitData.append('qualifications', formData.qualifications.join(', '));
    submitData.append('birth_year', formData.birthYear);
    submitData.append('birth_month', formData.birthMonth);
    submitData.append('birth_day', formData.birthDay);
    submitData.append('name', formData.name);
    submitData.append('postal_code', formData.postalCode);
    submitData.append('phone', formData.phone);

    try {
      const response = await fetch('https://readdy.ai/api/form/d6dql4qtehdqnvnpj2v0', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: submitData.toString(),
      });

      // === GAS DB送信 ===
      // qualification はカンマ区切りの1文字列として送信
      // birth_year / birth_month / birth_day は個別フィールドで送信
      // postal_code はハイフン除去して数字のみで送信
      // lp_id は lpTracking.ts 内で URL から自動取得
      const lpFormData: LPFormData = {
        qualification: formData.qualifications.join(', '),
        birth_year: formData.birthYear,
        birth_month: formData.birthMonth,
        birth_day: formData.birthDay,
        name: formData.name,
        postal_code: formData.postalCode,
        phone: formData.phone,
      };

      // LP_ID 引数は不要（URL から自動取得）
      const dbSuccess = await submitToDatabase(lpFormData, FORM_FIELDS);

      if (response.ok && dbSuccess) {
        sendGA4Event('nijikango04_form_complete');

        if (typeof (window as any)._lt === 'function') {
          (window as any)._lt('send', 'cv', {
            type: 'Conversion'
          }, ['e12bd2c2-7226-450e-94d5-04c55b56ecea']);
        }

        setSubmitSuccess(true);
      } else {
        setSubmitError(true);
      }
    } catch (error) {
      console.error('Form submission failed:', error);
      setSubmitError(true);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    onClose();
    setTimeout(() => {
      setCurrentStep(1);
      setSubmitSuccess(false);
      setSubmitError(false);
      setFormData({
        qualifications: [],
        birthYear: '',
        birthMonth: '',
        birthDay: '',
        name: '',
        postalCode: '',
        phone: '',
      });
    }, 300);
  };

  const StepIndicator = () => (
    <div className="flex justify-center gap-1.5 mt-6">
      {Array.from({ length: totalSteps }, (_, i) => (
        <span
          key={i}
          className={`h-2 rounded-full transition-all ${
            i + 1 === currentStep
              ? 'bg-pink-500 w-5'
              : i + 1 < currentStep
              ? 'bg-pink-300 w-2'
              : 'bg-gray-200 w-2'
          }`}
        />
      ))}
    </div>
  );

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[100] flex items-end justify-center">
      <div
        className="absolute inset-0 bg-black/50 transition-opacity"
        onClick={handleClose}
      />
      <div className="relative w-full max-w-[480px] bg-white rounded-t-3xl shadow-2xl animate-slide-up max-h-[90vh] overflow-y-auto">
        {/* Close button */}
        <button
          onClick={handleClose}
          className="absolute top-4 right-4 w-8 h-8 flex items-center justify-center rounded-full bg-gray-100 cursor-pointer z-10"
        >
          <i className="ri-close-line text-lg text-gray-500"></i>
        </button>

        <div className="px-5 pt-7 pb-8">
          {submitSuccess ? (
            <div className="text-center py-6">
              <div className="w-20 h-20 bg-pink-50 rounded-full flex items-center justify-center mx-auto mb-6">
                <i className="ri-check-double-line text-4xl text-pink-500"></i>
              </div>
              <h3 className="text-[20px] font-bold text-gray-900 mb-3 leading-[1.5]">
                診断申込みが<br />完了しました！
              </h3>
              <p className="text-[13px] text-gray-600 leading-[1.8] mb-2">
                ご登録いただいたお電話番号に、<br />
                担当アドバイザーからご連絡させていただきます。
              </p>
              <p className="text-[11px] text-gray-400 mb-8">
                ※通常1〜2営業日以内にご連絡いたします
              </p>
              <button
                onClick={handleClose}
                className="w-full bg-pink-500 text-white py-3.5 rounded-full font-bold text-[15px] whitespace-nowrap cursor-pointer active:bg-pink-600 transition-all"
              >
                閉じる
              </button>
            </div>
          ) : submitError ? (
            <div className="text-center py-6">
              <div className="w-20 h-20 bg-red-50 rounded-full flex items-center justify-center mx-auto mb-6">
                <i className="ri-error-warning-line text-4xl text-red-500"></i>
              </div>
              <h3 className="text-[20px] font-bold text-gray-900 mb-3 leading-[1.5]">
                送信に失敗しました
              </h3>
              <p className="text-[13px] text-gray-600 leading-[1.8] mb-8">
                申し訳ございません。<br />
                もう一度お試しください。
              </p>
              <button
                onClick={() => setSubmitError(false)}
                className="w-full bg-pink-500 text-white py-3.5 rounded-full font-bold text-[15px] whitespace-nowrap cursor-pointer active:bg-pink-600 transition-all"
              >
                戻る
              </button>
            </div>
          ) : (
            <>
              <div className="text-center mb-5">
                <h3 className="text-[18px] font-bold text-gray-900 mb-1">
                  無料 働き方診断
                </h3>
                <span className="text-[12px] text-pink-500 font-bold">
                  STEP {currentStep} / {totalSteps}
                </span>
              </div>

              <form id="diagnosis-popup-form" onSubmit={handleSubmit} data-readdy-form>

                {/* Step 1: 保有資格 */}
                {currentStep === 1 && (
                  <div>
                    <h4 className="text-[15px] font-bold text-gray-900 mb-1 text-center">
                      現在の保有資格を教えてください
                    </h4>
                    <p className="text-[11px] text-gray-500 text-center mb-5">複数選択可</p>

                    <div className="grid grid-cols-2 gap-3 mb-6">
                      {qualifications.map((qual) => (
                        <label
                          key={qual}
                          className={`flex items-center cursor-pointer p-3.5 border-2 rounded-xl transition-all text-[13px] ${
                            formData.qualifications.includes(qual)
                              ? 'border-pink-500 bg-pink-50'
                              : 'border-gray-200 bg-white'
                          }`}
                        >
                          <input
                            type="checkbox"
                            name="qualifications"
                            className="hidden"
                            checked={formData.qualifications.includes(qual)}
                            onChange={() => handleQualificationChange(qual)}
                          />
                          <span
                            className={`w-5 h-5 border-2 rounded-md mr-2.5 flex items-center justify-center flex-shrink-0 ${
                              formData.qualifications.includes(qual)
                                ? 'bg-pink-500 border-pink-500'
                                : 'border-gray-300'
                            }`}
                          >
                            {formData.qualifications.includes(qual) && (
                              <i className="ri-check-line text-white text-sm"></i>
                            )}
                          </span>
                          <span className="text-gray-700 font-medium">{qual}</span>
                        </label>
                      ))}
                    </div>

                    <button
                      id="form-step1-next"
                      type="button"
                      onClick={() => handleNextStep(1, 2)}
                      disabled={!canProceedStep1}
                      className="w-full bg-pink-500 text-white py-3.5 rounded-full font-bold text-[15px] transition-all disabled:opacity-40 disabled:cursor-not-allowed whitespace-nowrap cursor-pointer active:bg-pink-600"
                    >
                      次へ進む
                    </button>
                    <StepIndicator />
                  </div>
                )}

                {/* Step 2: 生年月日 */}
                {currentStep === 2 && (
                  <div>
                    <h4 className="text-[15px] font-bold text-gray-900 mb-1 text-center">
                      生年月日を教えてください
                    </h4>
                    <p className="text-[11px] text-gray-500 text-center mb-5">西暦でご入力ください</p>

                    <div className="flex gap-2 mb-6">
                      {/* 西暦 */}
                      <div className="relative flex-1">
                        <select
                          name="birthYear"
                          value={formData.birthYear}
                          onChange={(e) => setFormData((prev) => ({ ...prev, birthYear: e.target.value }))}
                          className="w-full border-2 border-gray-200 rounded-xl px-3 py-3.5 text-[13px] focus:border-pink-500 focus:outline-none bg-white appearance-none cursor-pointer"
                        >
                          <option value="">西暦</option>
                          {years.map((y) => (
                            <option key={y} value={y}>{y}年</option>
                          ))}
                        </select>
                        <div className="absolute right-2 top-1/2 -translate-y-1/2 pointer-events-none">
                          <i className="ri-arrow-down-s-line text-gray-400 text-lg"></i>
                        </div>
                      </div>

                      {/* 月 */}
                      <div className="relative w-[72px]">
                        <select
                          name="birthMonth"
                          value={formData.birthMonth}
                          onChange={(e) => setFormData((prev) => ({ ...prev, birthMonth: e.target.value }))}
                          className="w-full border-2 border-gray-200 rounded-xl px-2 py-3.5 text-[13px] focus:border-pink-500 focus:outline-none bg-white appearance-none cursor-pointer"
                        >
                          <option value="">月</option>
                          {months.map((m) => (
                            <option key={m} value={m}>{m}月</option>
                          ))}
                        </select>
                        <div className="absolute right-1.5 top-1/2 -translate-y-1/2 pointer-events-none">
                          <i className="ri-arrow-down-s-line text-gray-400 text-lg"></i>
                        </div>
                      </div>

                      {/* 日 */}
                      <div className="relative w-[72px]">
                        <select
                          name="birthDay"
                          value={formData.birthDay}
                          onChange={(e) => setFormData((prev) => ({ ...prev, birthDay: e.target.value }))}
                          className="w-full border-2 border-gray-200 rounded-xl px-2 py-3.5 text-[13px] focus:border-pink-500 focus:outline-none bg-white appearance-none cursor-pointer"
                        >
                          <option value="">日</option>
                          {days.map((d) => (
                            <option key={d} value={d}>{d}日</option>
                          ))}
                        </select>
                        <div className="absolute right-1.5 top-1/2 -translate-y-1/2 pointer-events-none">
                          <i className="ri-arrow-down-s-line text-gray-400 text-lg"></i>
                        </div>
                      </div>
                    </div>

                    <div className="flex gap-3">
                      <button
                        type="button"
                        onClick={() => setCurrentStep(1)}
                        className="w-1/3 bg-white text-gray-600 py-3.5 rounded-full font-bold text-[14px] border border-gray-200 whitespace-nowrap cursor-pointer active:bg-gray-50"
                      >
                        戻る
                      </button>
                      <button
                        id="form-step2-next"
                        type="button"
                        onClick={() => handleNextStep(2, 3)}
                        disabled={!canProceedStep2}
                        className="w-2/3 bg-pink-500 text-white py-3.5 rounded-full font-bold text-[15px] transition-all disabled:opacity-40 disabled:cursor-not-allowed whitespace-nowrap cursor-pointer active:bg-pink-600"
                      >
                        次へ進む
                      </button>
                    </div>
                    <StepIndicator />
                  </div>
                )}

                {/* Step 3: 氏名・郵便番号 */}
                {currentStep === 3 && (
                  <div>
                    <h4 className="text-[15px] font-bold text-gray-900 mb-5 text-center">
                      お名前とお住まいの郵便番号を<br />教えてください
                    </h4>

                    <div className="space-y-4 mb-6">
                      <div>
                        <label className="block text-[12px] font-medium text-gray-600 mb-1.5">
                          お名前<span className="text-[10px] text-gray-400 ml-1">（仮名OK）</span>
                        </label>
                        <input
                          type="text"
                          name="name"
                          value={formData.name}
                          onChange={(e) => setFormData((prev) => ({ ...prev, name: e.target.value }))}
                          placeholder="例：山田 花子"
                          className="w-full border-2 border-gray-200 rounded-xl px-4 py-3.5 text-[14px] focus:border-pink-500 focus:outline-none bg-white"
                        />
                      </div>
                      <div>
                        <label className="block text-[12px] font-medium text-gray-600 mb-1.5">
                          お住まいの郵便番号<span className="text-[10px] text-gray-400 ml-1">（7桁）</span>
                        </label>
                        <input
                          type="text"
                          name="postalCode"
                          value={formData.postalCode}
                          onChange={(e) => handlePostalCodeChange(e.target.value)}
                          placeholder="150-0001"
                          maxLength={8}
                          className="w-full border-2 border-gray-200 rounded-xl px-4 py-3.5 text-[14px] focus:border-pink-500 focus:outline-none bg-white"
                        />
                        <p className={`text-[11px] mt-1.5 ${
                          formData.postalCode.replace(/\D/g, '').length === 7
                            ? 'text-pink-500'
                            : 'text-gray-400'
                        }`}>
                          {formData.postalCode.replace(/\D/g, '').length} / 7桁
                        </p>
                      </div>
                    </div>

                    <div className="flex gap-3">
                      <button
                        type="button"
                        onClick={() => setCurrentStep(2)}
                        className="w-1/3 bg-white text-gray-600 py-3.5 rounded-full font-bold text-[14px] border border-gray-200 whitespace-nowrap cursor-pointer active:bg-gray-50"
                      >
                        戻る
                      </button>
                      <button
                        id="form-step3-next"
                        type="button"
                        onClick={() => handleNextStep(3, 4)}
                        disabled={!canProceedStep3}
                        className="w-2/3 bg-pink-500 text-white py-3.5 rounded-full font-bold text-[15px] transition-all disabled:opacity-40 disabled:cursor-not-allowed whitespace-nowrap cursor-pointer active:bg-pink-600"
                      >
                        次へ進む
                      </button>
                    </div>
                    <StepIndicator />
                  </div>
                )}

                {/* Step 4: 電話番号 */}
                {currentStep === 4 && (
                  <div>
                    <h4 className="text-[15px] font-bold text-gray-900 mb-1 text-center">
                      電話番号を教えてください
                    </h4>
                    <p className="text-[11px] text-gray-500 text-center mb-5">担当アドバイザーからご連絡します</p>

                    <div className="mb-6">
                      <label className="block text-[12px] font-medium text-gray-600 mb-1.5">
                        電話番号<span className="text-[10px] text-gray-400 ml-1">（11桁）</span>
                      </label>
                      <input
                        type="tel"
                        name="phone"
                        value={formData.phone}
                        onChange={(e) => handlePhoneChange(e.target.value)}
                        placeholder="090-1234-5678"
                        maxLength={13}
                        className="w-full border-2 border-gray-200 rounded-xl px-4 py-3.5 text-[14px] focus:border-pink-500 focus:outline-none bg-white"
                      />
                      <p className={`text-[11px] mt-1.5 ${
                        formData.phone.replace(/\D/g, '').length === 11
                          ? 'text-pink-500'
                          : 'text-gray-400'
                      }`}>
                        {formData.phone.replace(/\D/g, '').length} / 11桁
                      </p>
                    </div>

                    <div className="flex gap-3">
                      <button
                        type="button"
                        onClick={() => setCurrentStep(3)}
                        className="w-1/3 bg-white text-gray-600 py-3.5 rounded-full font-bold text-[14px] border border-gray-200 whitespace-nowrap cursor-pointer active:bg-gray-50"
                      >
                        戻る
                      </button>
                      <button
                        id="form-step4-submit"
                        type="submit"
                        disabled={!canSubmit || isSubmitting}
                        className="w-2/3 bg-pink-500 text-white py-3.5 rounded-full font-bold text-[14px] transition-all disabled:opacity-40 disabled:cursor-not-allowed whitespace-nowrap cursor-pointer active:bg-pink-600"
                      >
                        {isSubmitting ? '送信中...' : '[無料] 診断結果を受け取る'}
                      </button>
                    </div>
                    <StepIndicator />
                  </div>
                )}
              </form>
            </>
          )}
        </div>
      </div>
    </div>
  );
}